import { hmac } from "@noble/hashes/hmac.js";
import { sha256 } from "@noble/hashes/sha2.js";

import {
  PUBLIC_PET_PALETTE_VALUES,
  PUBLIC_PET_PATTERN_VALUES,
} from "./constants";
import type { PublicPetAppearanceV1 } from "./schema";

function digestHex(value: string): string {
  return [...sha256(new TextEncoder().encode(value))]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function keyedDigestHex(key: string, value: string): string {
  return [...hmac(sha256, new TextEncoder().encode(key), new TextEncoder().encode(value))]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Produces a share-scoped pseudonym. The source identity is used only inside
 * this one-way projection and is never copied into the public contract.
 */
export function publicPetIdForSourceV1(
  privacySeed: string,
  sourceIdentity: string,
): string {
  return `pet_${keyedDigestHex(
    privacySeed,
    `banryeojusik/public-pet/v1\u0000${sourceIdentity}`,
  ).slice(0, 24)}`;
}

/**
 * Hidden-identity pets use a neutral, session-specific appearance. This keeps
 * a user's custom palette/hat from becoming a cross-link fingerprint.
 */
export function neutralAppearanceForPublicPetV1(
  publicSeed: string,
  publicPetId: string,
): PublicPetAppearanceV1 {
  const digest = digestHex(
    `banryeojusik/public-appearance/v1\u0000${publicSeed}\u0000${publicPetId}`,
  );
  const paletteIndex =
    Number.parseInt(digest.slice(0, 8), 16) %
    PUBLIC_PET_PALETTE_VALUES.length;
  const patternIndex =
    Number.parseInt(digest.slice(8, 16), 16) %
    PUBLIC_PET_PATTERN_VALUES.length;

  return {
    paletteId: PUBLIC_PET_PALETTE_VALUES[paletteIndex]!,
    patternId: PUBLIC_PET_PATTERN_VALUES[patternIndex]!,
  };
}
